import React, { useState, useEffect } from "react";
import {
  DevicesOther,
  Smartphone,
  LocationOn,
  Schedule,
  NetworkWifi,
  Apps,
  Close,
  Delete,
  PowerSettingsNew,
} from "@mui/icons-material";
import "./SessionManagementModal.scss";
import { SessionManagmentServices } from "../../services/SessionManagmentServices";
import { getUser } from "../../config/user";

const SessionManagementModal = ({ visible, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Get current session ID when component mounts or becomes visible
  useEffect(() => {
    if (visible) {
      const user = getUser();
      setCurrentSessionId(user?.session?.sid);
    }
  }, [visible]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await SessionManagmentServices.getUserSessionsList();

      if (response?.sessions) {
        const formattedSessions = response.sessions.map((session) => ({
          ...session,
          current: session.sid === currentSessionId,
          deviceName: session.deviceName || "Unknown Device",
          location: session.location || "Location not available",
          browser: session.browser || "Unknown Browser",
          appType: session.appType || "Unknown Platform",
        }));
        setSessions(formattedSessions);
      }
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && currentSessionId) {
      fetchSessions();
    }
  }, [visible, currentSessionId]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  const handlelogoutSession = async (sessionId) => {
    try {
      await SessionManagmentServices.terminateSession([sessionId], false);
      fetchSessions();
      setConfirmAction(null);
      window.alert("Session logout successfully");
    } catch (error) {
      window.alert("Failed to logout session");
      console.error("Failed to logout session:", error);
    }
  };

  const handlelogoutAll = async () => {
    try {
      await SessionManagmentServices.terminateSession([currentSessionId], true);
      fetchSessions();
      setConfirmAction(null);
      window.alert("All other sessions logout successfully");
    } catch (error) {
      window.alert("Failed to logout sessions");
      console.error("Failed to logout sessions:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDeviceIcon = (session) => {
    if (session.appType.toLowerCase().includes("mobile")) {
      return <Smartphone className="device-icon" />;
    }
    return <DevicesOther className="device-icon" />;
  };

  const getPlatformInfo = (session) => {
    if (session.browser?.toLowerCase() === session.appType?.toLowerCase()) {
      return session.appType;
    }
    return `${session.browser || "Unknown"} • ${session.appType || "Unknown"}`;
  };

  if (!visible) return null;

  return (
    <div className="session-modal-overlay">
      <div className="session-modal-container">
        <div className="modal-header font-lufga">
     

          <h2>{sessions.length} active session{sessions.length !== 1 ? 's' : ''}</h2>
       
          <button className="close-button font-lufga" onClick={onClose}>
            <Close />
          </button>
      
        </div>
       
        <div className="modal-content mt-1">
          {loading ? (
            <div className="loading-state font-lufga">
              <div className="spinner"></div>
              <p>Loading sessions...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="empty-state font-lufga">
              <DevicesOther className="empty-icon" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <>
              <div className="sessions-list">
                {sessions.map((session) => (
                  <div
                    key={session.sid}
                    className={`session-card ${
                      session.current ? "current" : ""
                    }`}
                  >
                    <div className="session-header font-lufga">
                      <div className="device-info">
                        {getDeviceIcon(session)}
                        <div>
                          <h3>{session.deviceName}</h3>
                          <span className="session-status">
                            {session.current
                              ? "(Current device)"
                              : `Active since ${formatDate(session.updatedAt)}`}
                          </span>
                        </div>
                      </div>
                      {!session.current && (
                        <button
                          className="action-button danger font-lufga"
                          onClick={() =>
                            setConfirmAction({
                              type: "single",
                              id: session.sid,
                            })
                          }
                        >
                          <PowerSettingsNew fontSize="small" />
                          Logout
                        </button>
                      )}
                    </div>

                    <div className="session-details font-lufga">
                      <div className="detail-item">
                        <LocationOn fontSize="small" />
                        <span>{session.location}</span>
                      </div>
                      <div className="detail-item">
                        <NetworkWifi fontSize="small" />
                        <span>{session.ip}</span>
                      </div>
                      <div className="detail-item">
                        <Apps fontSize="small" />
                        <span>{getPlatformInfo(session)}</span>
                      </div>
                    </div>

                    {confirmAction?.type === "single" &&
                      confirmAction.id === session.sid && (
                        <div className="confirmation-dialog">
                          <p>Are you sure you want to logout this session?</p>
                          <div className="confirmation-actions">
                            <button
                              className="cancel-button"
                              onClick={() => setConfirmAction(null)}
                            >
                              Cancel
                            </button>
                            <button
                              className="confirm-button danger font-lufga"
                              onClick={() => handlelogoutSession(session.sid)}
                            >
                              logout Session
                            </button>
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>

              {sessions.filter((s) => !s.current).length > 0 && (
                <>
                  {confirmAction?.type === "all" ? (
                    <div className="confirmation-dialog full-width">
                      <p>Are you sure you want to log out all other devices?</p>
                      <div className="confirmation-actions">
                        <button
                          className="cancel-button"
                          onClick={() => setConfirmAction(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="confirm-button danger font-lufga"
                          onClick={handlelogoutAll}
                        >
                          logout All Sessions (excpet me)
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="terminate-all-button font-lufga"
                      onClick={() => setConfirmAction({ type: "all" })}
                    >
                      <Delete fontSize="small" />
                      Log Out All Other Devices
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionManagementModal;
